<template>
    <div class="row pt-2">
        <div class="col">
            <h3 class="mt-4">
                What income do you want to aim for?
                <button
                    ref="collapseButton" class="btn" type="button" @click="toggleCollapse"
                    :aria-expanded="isExpanded" aria-controls="guidanceContent"
                    aria-label="Toggle display of help with starting income">
                    <i class="arrowhead bi bi-chevron-down ms-auto" :class="{ 'rotate-arrow': isExpanded }"></i>
                </button>
            </h3>
        </div>
    </div>
    <transition name="fade">
        <div v-show="isExpanded" id="guidanceContent">
            <div class="row">
                <div class="col">
                    <p class="text-muted mt-2 mb-0">
                        Answer the questions below to get a suggested retirement income after tax.
                        <help title="After tax income" :text="HelpText.ReqmntAfterTaxIncome()" />
                    </p>
                </div>
            </div>
            <div class="row pt-4">
                <div class="col">
                    <options-question name="liveInLondon" question="Do you live in Greater London (as it's more expensive there)?"
                        :options="yesNoOptions" v-model:selected="liveInLondonModel" @changed="calculateSuggestedAmounts"
                        :width="getYesNoWidth()" />
                </div>
            </div>
            <!-- <div class="row pt-4">
                <div class="col">
                    <options-question name="supportingPartner"
                        question="Do you need your own income to be higher to support someone with no income?"
                        :options="yesNoOptions" v-model:selected="supportingPartnerModel" @changed="calculateSuggestedAmounts"
                        :width="getYesNoWidth()" />
                </div>
            </div> -->
            <div class="row pt-4">
                <div class="col">
                    <options-question name="lifestyle" question="Which type of retirement do you want to aim for?"
                        :options="['Minimum', 'Moderate', 'Comfortable']" v-model:selected="lifestyleModel"
                        @changed="calculateSuggestedAmounts" :width="75" />
                </div>
            </div>
            <div class="row pt-2">
                <div class="col">
                    <div class="alert alert-info">
                        <p class="mb-0"><strong>Minimum</strong> - covers all your needs, with some left over for fun</p>
                        <p class="mb-0"><strong>Moderate</strong> - more financial security and flexibility</p>
                        <p class="mb-2"><strong>Comfortable</strong> - more financial freedom and some luxuries</p>
                        <p class="mb-0"><a href="https://www.retirementlivingstandards.org.uk" target="_blank">Visit the Retirement Living Standards website</a> to find out more.</p>
                    </div>
                </div>
            </div>
            <div v-if="showDescription" class="row pt-2">
                <div class="col">
                    <income-guidance-summary :income-setup-mode="false"
                        :suggested-amount-on-plsa-date="suggestedAmountOnPLSADate"
                        :suggested-amount-at-retirement="suggestedAmountAtRetirement">
                    </income-guidance-summary>
                </div>
            </div>
        </div>
    </transition>
</template>

<script setup lang="ts">
import { ref, watch, computed, onMounted } from "vue";
import OptionsQuestion from "@/guiide/components/common/OptionsQuestion.vue";
import IncomeGuidanceSummary from "./IncomeGuidanceSummary.vue";
import Help from "@/guiide/components/common/Help.vue";
import { Retiree } from "../../ts/retiree";
import { mapBooleanToYesNo, mapYesNoToBoolean } from "@/guiide/ts/utilities";
import Lifestyle from "@/guiide/ts/lifestyle";
import { SuggestedAmounts } from "../../ts/wants/suggestedAmounts";
import IncomeCalculator from "@/guiide/ts/incomeCalculator";
import HelpText from "@/guiide/ts/helpText";
import { IncomeGuidanceAnswers } from "@/guiide/ts/wants/incomeGuidanceAnswers";
import { yesNoOptions, getYesNoWidth } from "../../ts/utilities";
import { useStore } from "vuex";

defineExpose({ getAnswers });

const store = useStore();
const calculator = new IncomeCalculator();

const liveInLondonModel = ref("No");
const supportingPartnerModel = ref("No");
const lifestyleModel = ref("Moderate");

const suggestedAmountOnPLSADate = ref(0);
const suggestedAmountAtRetirement = ref(0);

const isExpanded = ref(false);

const emit = defineEmits<{ (e: 'incomeGuidanceChanged', value: { suggestedAmountOnPLSADate: number, suggestedAmountAtRetirement: number }): void }>();

const props = defineProps({
    showDescription: { type: Boolean, default: false },
    displayContent: { type: Boolean, default: false }
});

onMounted(() => {
    isExpanded.value = props.displayContent;
});

watch(() => store.state.retiree, (newRetiree: Retiree) => {
    if (newRetiree == null) {
        return;
    }

    liveInLondonModel.value = mapBooleanToYesNo(
        newRetiree.requirements.livesInLondon
    );

    supportingPartnerModel.value = mapBooleanToYesNo(
        newRetiree.requirements.isSupportingPartner
    );

    lifestyleModel.value = newRetiree.requirements.targetLifestyle;

    calculateSuggestedAmounts();
});

watch(() => props.displayContent, (newValue: boolean) => {
    if (newValue == isExpanded.value) {
        return;
    }

    isExpanded.value = newValue;
});

const retirementDate = computed<Date | null>(() => {
    if (store.state.retiree == null) {
        return null;
    }
    return new Date(store.state.retiree.retirementDate);
});

function calculateSuggestedAmounts() {
    if (retirementDate.value == null) {
        return;
    }

    const answers = getAnswers();

    suggestedAmountOnPLSADate.value = calculator.getBaseAmount(
        answers.livesInLondon,
        answers.supportingPartner,
        answers.lifestyle);

    suggestedAmountAtRetirement.value = calculator.calcSuggestedRetirementIncome(
        answers.livesInLondon,
        answers.supportingPartner,
        answers.lifestyle,
        retirementDate.value
    );

    const eventInfo: SuggestedAmounts = {
        suggestedAmountOnPLSADate: suggestedAmountOnPLSADate.value,
        suggestedAmountAtRetirement: suggestedAmountAtRetirement.value
    };
    emit('incomeGuidanceChanged', eventInfo);
}

function toggleCollapse() {
    isExpanded.value = !isExpanded.value;
}

function getAnswers(): IncomeGuidanceAnswers {
    const livesInLondon = mapYesNoToBoolean(liveInLondonModel.value);
    const supportingPartner = mapYesNoToBoolean(supportingPartnerModel.value);
    const lifestyle: Lifestyle = Lifestyle[lifestyleModel.value as keyof typeof Lifestyle];
    return { livesInLondon, supportingPartner, lifestyle };
}
</script>

<style scoped>
.arrowhead {
    cursor: pointer;
    display: inline-block;
}

.rotate-arrow {
    transform: rotate(180deg);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease, transform 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}
</style>
